import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LifeBuoy, Plus, Loader2, Paperclip, X, MessageSquare } from "lucide-react";
import { SupportTicketDetailAdmin } from "@/components/support/SupportTicketDetailAdmin";
import { SupportAdminSection } from "@/components/support/SupportAdminSection";
import { useAuth } from "@/context/AuthContext";
import {
  SUPPORT_CATEGORIES, STATUS_META, PRIORITY_META, fmtDateTime, uploadSupportFile, fmtTicketNo,
} from "@/components/support/supportUtils";

export default function Assistenza() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (isSuper) { setLoading(false); return; }
    setLoading(true);
    try { const r = await api.get("/support/tickets"); setTickets(r.data || []); }
    catch { toast.error("Impossibile caricare i ticket"); }
    finally { setLoading(false); }
  }, [isSuper]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fade-in" data-testid="assistenza-page">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Supporto</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-head text-4xl font-extrabold tracking-tighter flex items-center gap-3"><LifeBuoy className="h-8 w-8 text-[#007BFF]" />Assistenza</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">{isSuper ? "Gestisci i ticket di aiuto ricevuti da tutti gli amministratori: rispondi e aggiorna lo stato." : "Apri un ticket di aiuto: il nostro team ti risponde qui, con tutta la conversazione in un unico posto."}</p>
          </div>
          {!isSuper && <Button data-testid="open-new-ticket-btn" onClick={() => setOpenNew(true)} className="bg-[#007BFF] hover:bg-[#0056B3]"><Plus className="h-4 w-4 mr-2" />Apri ticket</Button>}
        </div>
      </header>

      <div className="p-4 sm:p-10">
        {isSuper ? (
          <SupportAdminSection />
        ) : selected ? (
          <SupportTicketDetailAdmin ticketId={selected} onBack={() => { setSelected(null); load(); }} onChanged={load} />
        ) : loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="assistenza-empty">
            <LifeBuoy className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Non hai ancora aperto nessun ticket.</p>
            <p className="text-xs mt-1">Hai bisogno di aiuto? Apri il tuo primo ticket.</p>
          </div>
        ) : (
          <div className="space-y-3 max-w-3xl">
            {tickets.map((t) => {
              const st = STATUS_META[t.status] || STATUS_META.aperto;
              const pr = PRIORITY_META[t.priority] || PRIORITY_META.media;
              return (
                <button key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => setSelected(t.id)}
                  className="w-full text-left bg-card border border-border rounded-xl p-4 hover:border-[#007BFF]/50 transition-colors flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#007BFF]/10 flex items-center justify-center shrink-0"><MessageSquare className="h-4 w-4 text-[#007BFF]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.ticket_no != null && <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-white">{fmtTicketNo(t.ticket_no)}</span>}
                      <span className="font-semibold text-sm truncate">{t.subject}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pr.cls}`}>{pr.label}</span>
                      {t.unread > 0 && <span data-testid={`ticket-unread-${t.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">{t.unread} nuovo{t.unread === 1 ? "" : "i"}</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">{t.category} · {t.last_message_preview || "—"}</div>
                    <div className="text-[10px] text-muted-foreground/70 mt-0.5">Aggiornato il {fmtDateTime(t.last_message_at)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <NewTicketDialog open={openNew} onClose={() => setOpenNew(false)} onCreated={(id) => { setOpenNew(false); load(); setSelected(id); }} />
    </div>
  );
}

const NewTicketDialog = ({ open, onClose, onCreated }) => {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("Altro");
  const [priority, setPriority] = useState("media");
  const [description, setDescription] = useState("");
  const [atts, setAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) { setSubject(""); setCategory("Altro"); setPriority("media"); setDescription(""); setAtts([]); }
  }, [open]);

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: troppo grande (max 10 MB)`); continue; }
      try { const rec = await uploadSupportFile(f); setAtts((p) => [...p, rec]); }
      catch { toast.error(`Caricamento di ${f.name} non riuscito`); }
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!subject.trim()) { toast.error("Inserisci l'oggetto"); return; }
    if (!description.trim()) { toast.error("Inserisci una descrizione"); return; }
    setBusy(true);
    try {
      const r = await api.post("/support/tickets", { subject: subject.trim(), category, priority, description: description.trim(), attachments: atts });
      toast.success("Ticket aperto");
      onCreated(r.data.ticket.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Creazione non riuscita"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-ticket-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-[#007BFF]" />Apri un ticket di assistenza</DialogTitle>
          <DialogDescription>Descrivi il problema: ti risponderemo il prima possibile.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Oggetto *</label>
            <Input data-testid="ticket-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Riepilogo del problema" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Categoria</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="ticket-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{SUPPORT_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`ticket-cat-${c}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Priorità</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="ticket-priority" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bassa" data-testid="ticket-prio-bassa">Bassa</SelectItem>
                  <SelectItem value="media" data-testid="ticket-prio-media">Media</SelectItem>
                  <SelectItem value="alta" data-testid="ticket-prio-alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Descrizione *</label>
            <Textarea data-testid="ticket-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descrivi cosa succede, cosa ti aspettavi e (se utile) i passaggi per riprodurre il problema." className="mt-1 min-h-[110px]" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Allegato (facoltativo)</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="ticket-file" onChange={onAttach} />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-md">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Paperclip className="h-4 w-4 mr-2" />}Allega file
              </Button>
              {atts.map((a) => (
                <span key={a.file_id} className="inline-flex items-center gap-1.5 text-xs bg-secondary border border-border rounded-lg px-2 py-1">
                  <span className="truncate max-w-[140px]">{a.filename}</span>
                  <button onClick={() => setAtts((p) => p.filter((x) => x.file_id !== a.file_id))} className="hover:bg-background rounded-full p-0.5"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button data-testid="ticket-submit" onClick={submit} disabled={busy} className="bg-[#007BFF] hover:bg-[#0056B3]">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Invia ticket
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
